import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FaWhatsapp } from "react-icons/fa";
import { ChatBubbleLeftEllipsisIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

export interface IBookingLinkSentResult {
  mobile: string;
  whatsappSent: boolean;
  smsSent: boolean;
}

interface IBookingLinkSentModalProps {
  open: boolean;
  result: IBookingLinkSentResult | null;
  subServiceName?: string | null;
  onClose: () => void;
}

const formatMobile = (mobile: string) => {
  const digits = mobile.replace(/\D/g, "").slice(-10);
  return digits.length === 10 ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` : mobile;
};

const ChannelStatus: React.FC<{ label: string; sent: boolean; icon: React.ReactNode }> = ({ label, sent, icon }) => (
  <div className="flex items-center justify-between rounded-[10px] border border-[#E9ECF2] px-3 py-2.5">
    <div className="flex items-center gap-2 text-[12px] font-medium text-[#000000]">
      {icon}
      {label}
    </div>
    <span
      className={`rounded-[6px] px-2 py-0.5 text-[10px] font-semibold ${
        sent ? "bg-[#E7F8EE] text-[#128C4B]" : "bg-[#FEF3F2] text-[#B42318]"
      }`}
    >
      {sent ? "Sent" : "Not sent"}
    </span>
  </div>
);

const BookingLinkSentModal: React.FC<IBookingLinkSentModalProps> = ({ open, result, subServiceName, onClose }) => {
  const failedChannel = result && (!result.whatsappSent ? "WhatsApp" : !result.smsSent ? "SMS" : null);
  const deliveredChannel = failedChannel === "WhatsApp" ? "SMS" : "WhatsApp";
  const serviceLabel = subServiceName?.trim() || "At Home Service";

  return (
    <Transition.Root show={open && Boolean(result)} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="flex min-h-screen items-center justify-center px-4 py-8">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full max-w-[400px] transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
              {result && (
                <>
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E7F8EE]">
                      <CheckCircleIcon className="h-7 w-7 text-[#12B76A]" />
                    </div>
                    <Dialog.Title as="h3" className="mt-3 text-[16px] font-semibold text-[#000000]">
                      Booking link sent
                    </Dialog.Title>
                    <p className="mt-1 text-[12px] text-[#6C727F]">
                      {serviceLabel} link sent to{" "}
                      <span className="font-semibold text-[#000000]">{formatMobile(result.mobile)}</span>
                    </p>
                  </div>
                  <div className="mt-5 space-y-2">
                    <ChannelStatus
                      label="WhatsApp"
                      sent={result.whatsappSent}
                      icon={<FaWhatsapp className="h-4 w-4 text-[#25D366]" />}
                    />
                    <ChannelStatus
                      label="SMS"
                      sent={result.smsSent}
                      icon={<ChatBubbleLeftEllipsisIcon className="h-4 w-4 text-[#0B6EFF]" />}
                    />
                  </div>
                  {failedChannel && (
                    <div className="mt-3 flex gap-2 rounded-[10px] bg-[#FFFAEB] px-3 py-2.5 text-[11px] text-[#93370D]">
                      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                      <p>
                        {failedChannel} could not be sent. The pet parent can still book from the {deliveredChannel}{" "}
                        message.
                      </p>
                    </div>
                  )}
                  <div className="mt-4 rounded-[10px] bg-[#F0F6FF] px-3 py-2.5">
                    <p className="text-[11px] font-semibold text-[#000000]">What happens next</p>
                    <p className="mt-0.5 text-[11px] text-[#6C727F]">
                      The pet parent opens the link, picks a slot in the Vetic app and confirms the booking there. Ask
                      them to check {deliveredChannel} if they can't find it.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-5 w-full rounded-[8px] bg-[#000000] py-[10px] text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    Done
                  </button>
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BookingLinkSentModal;
